import { FC, Fragment, useCallback, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import {
  $CurrentAvailableDiscoverDataOrNull,
  $CurrentDiscoverDataAsAvailable,
  $DiscoverShownPostCursor,
  DISCOVER_SHOWN_POST_BIAS,
} from '@discover/shared/atom';
import { ImmersivePostItem } from './immersive-post-item';
import { useViewTracking } from '~/utils/use-view-tracking';
import { ModuleType } from '~/utils/module-type';
import { formatSearchWords } from '~/components/search-words/util';
import { $LibraDiscoverPostLazyLoad } from '~/utils/libra';
import { ImmersivePostRichCard } from './immersive-post-rich-card';
import { useSearchParams } from '@remix-run/react';
import { $UserAgent } from '@lemon8/web-app-shared/atom';

export enum ERichCardType {
  SEARCH_WORDS = 1,
  CLASSIFY_KEYWORDS = 2,
  HASHTAG_CARD = 3,
  DEFAULT = 4,
}

export const DiscoverImmersivePostSectionImpl: FC = () => {
  const { isBot } = useAtomValue($UserAgent);
  const postCursor = useAtomValue($DiscoverShownPostCursor);
  const data = useAtomValue($CurrentDiscoverDataAsAvailable);
  const enableDiscoverPostLazyLoad = useAtomValue($LibraDiscoverPostLazyLoad);

  const [search] = useSearchParams();
  const postCount = search.get('post_count') || data.article_v2.items.length;

  const { items: originalArticleItems } = data.article_v2;
  const items = originalArticleItems.slice(0, Number(postCount));
  const { posList } = useMemo(() => formatSearchWords(data.guessWordCards), [data]);

  const observedRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_module_show', {
        current_page_module: ModuleType.related_article,
      });
    }, []),
  });

  const { richCards } = useMemo(() => {
    const richCards = [];

    for (let i = 0; i < items.length; i++) {
      richCards[i] = {
        ...items[i],
        extraElement: {
          elementType: ERichCardType.DEFAULT,
          elementInsertedPos: i,
        },
      };
      switch (i) {
        case 4:
          richCards[i] = {
            ...items[i],
            extraElement: {
              elementType: ERichCardType.CLASSIFY_KEYWORDS,
              elementInsertedPos: i,
            },
          };
          break;
        default:
          break;
      }
      if (posList.includes(`${i}`)) {
        richCards[i] = {
          ...items[i],
          extraElement: {
            elementType: ERichCardType.SEARCH_WORDS,
            elementInsertedPos: i,
          },
        };
      }
    }

    return {
      richCards,
    };
  }, []);

  return (
    <section key="kep-immersive-posts-section" id="immersive-posts" ref={observedRef}>
      {richCards.map(({ article, author, comments, extraElement }, index) => {
        const { elementType } = extraElement;

        if (enableDiscoverPostLazyLoad && index >= postCursor + DISCOVER_SHOWN_POST_BIAS && !isBot) {
          return null;
        }

        return (
          <Fragment key={article.groupId}>
            <ImmersivePostItem
              article={article}
              author={author}
              comments={comments}
              rank={index}
              beforeGuessWordsCards={posList.includes(`${index}`)}
              beforeHotKeywordsModule={index === 4}
              beforeHashtagArticleCards={elementType === ERichCardType.HASHTAG_CARD}
            />
            <ImmersivePostRichCard {...extraElement} />
          </Fragment>
        );
      })}
    </section>
  );
};

export const DiscoverImmersivePostSection: FC = () => {
  const data = useAtomValue($CurrentAvailableDiscoverDataOrNull);

  if (!data) {
    return null;
  }

  return <DiscoverImmersivePostSectionImpl />;
};
